import { Routes, Route, Navigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { useAuthStore } from './stores/authStore';
import { useThemeStore } from './stores/themeStore';
import { useEffect } from 'react';

// Layout
import { ClientLayout } from './components/layout/ClientLayout';

// Pages
import { LoginPage } from './routes/login';
import { VerifyPage } from './routes/verify';
import { ProjectsPage } from './routes/index';
import { ProjectsListPage } from './routes/projects/index';
import { ProjectDetailPage } from './routes/projects/[id]';
import { WorkItemPage } from './routes/work-items/[id]';
import { RequestsPage } from './routes/requests/index';
import { NewRequestPage } from './routes/requests/new';
import { RequestDetailPage } from './routes/requests/[id]';
import { BundlesPage } from './routes/bundles/index';
import { BundleDetailPage } from './routes/bundles/[id]';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-bg">
      <div className="relative">
        <div className="w-16 h-16 border-4 border-accent/20 rounded-full" />
        <div className="absolute inset-0 w-16 h-16 border-4 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    </div>
  );
}

export default function App() {
  const location = useLocation();
  const { theme } = useThemeStore();
  const { checkAuth } = useAuthStore();

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }, [theme]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/verify" element={<VerifyPage />} />

        <Route
          path="/"
          element={
            <ProtectedRoute>
              <ClientLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<ProjectsPage />} />
          <Route path="projects" element={<ProjectsListPage />} />
          <Route path="projects/:id" element={<ProjectDetailPage />} />
          <Route path="work-items/:id" element={<WorkItemPage />} />
          <Route path="requests" element={<RequestsPage />} />
          <Route path="requests/new" element={<NewRequestPage />} />
          <Route path="requests/:id" element={<RequestDetailPage />} />
          <Route path="bundles" element={<BundlesPage />} />
          <Route path="bundles/:id" element={<BundleDetailPage />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AnimatePresence>
  );
}
